import { isCanonicalUploadTime, isPositiveSafeId, isPreparationAnalysisOutcome, type PreparationAnalysis } from "./metadata";
import type { OpeningWarning } from "./prepareOpeningRepertoire";
import { readUserComment } from "./userComment";

export type AnalysisFlag = "archived" | "favorite";
export type AnalysisFieldChange = Readonly<{ field: AnalysisFlag; value: boolean }> |
  Readonly<{ field: "userComment"; value: string | null }>;

export type SkippedAnalysisGame = {
  gameIndex: number;
  code: "student-color-unknown" | "move-limit" | "unsupported-variant" | "invalid-fen" | "illegal-move";
  move?: string;
  movePath?: string[];
};

export type ImportReport = {
  problemSetId: number | null;
  importedCount: number;
  skippedGames: SkippedAnalysisGame[];
  openingWarnings: OpeningWarning[];
};

export type GameAnalysisListItem = Readonly<{
  problemSetId: number;
  problemId: number;
  title: string;
  uploadedAt: string | null;
  studentColor: "white" | "black" | null;
  studentScore: 0 | 0.5 | 1 | null;
  openingId: number | null;
  analysis: PreparationAnalysis | null;
  archived: boolean;
  favorite: boolean;
  userComment: string | null;
}>;

export type GameAnalysisListResponse = Readonly<{
  games: readonly GameAnalysisListItem[];
  openingGroups: readonly Readonly<{
    problemSetId: number;
    name: string;
    openings: readonly Readonly<{ problemId: number; title: string }>[];
  }>[];
}>;

function object(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validGame(value: unknown): value is GameAnalysisListItem {
  if (!object(value)) return false;
  return isPositiveSafeId(value.problemSetId) && isPositiveSafeId(value.problemId) && typeof value.title === "string" &&
    (value.uploadedAt === null || isCanonicalUploadTime(value.uploadedAt)) &&
    (value.studentColor === null || value.studentColor === "white" || value.studentColor === "black") &&
    (value.studentScore === null || value.studentScore === 0 || value.studentScore === 0.5 || value.studentScore === 1) &&
    (value.studentColor !== null || value.studentScore === null) &&
    (value.openingId === null || isPositiveSafeId(value.openingId)) &&
    (value.analysis === null || (object(value.analysis) && isPreparationAnalysisOutcome(value.analysis.outcome) &&
      (value.analysis.comment === null || (typeof value.analysis.comment === "string" && value.analysis.comment.trim().length > 0)))) &&
    typeof value.archived === "boolean" && typeof value.favorite === "boolean" &&
    (value.userComment === null || (typeof value.userComment === "string" &&
      readUserComment(value.userComment) === value.userComment));
}

/** Reject the complete read when its required normalized contract is malformed. */
export function decodeGameAnalysisListResponse(value: unknown): GameAnalysisListResponse {
  if (!object(value) || !Array.isArray(value.games) || !value.games.every(validGame) || !Array.isArray(value.openingGroups)) {
    throw new Error("Invalid game analysis list response");
  }
  if (new Set(value.games.map((game: GameAnalysisListItem) => game.problemId)).size !== value.games.length) {
    throw new Error("Duplicate game analysis game");
  }
  const groupIds = new Set<number>();
  const openingIds = new Set<number>();
  for (const group of value.openingGroups) {
    if (!object(group) || !isPositiveSafeId(group.problemSetId) || typeof group.name !== "string" ||
      !Array.isArray(group.openings) || !group.openings.every((opening: unknown) => object(opening) &&
        isPositiveSafeId(opening.problemId) && typeof opening.title === "string")) {
      throw new Error("Invalid game analysis opening catalog");
    }
    if (groupIds.has(group.problemSetId)) throw new Error("Duplicate opening group");
    groupIds.add(group.problemSetId);
    for (const opening of group.openings as Array<{ problemId: number }>) {
      if (openingIds.has(opening.problemId)) throw new Error("Ambiguous opening membership");
      openingIds.add(opening.problemId);
    }
  }
  return value as GameAnalysisListResponse;
}
